import { TariffTimelineSegment, TariffWeekDay } from '@/types/ChargingPoint'

const WEEKDAY_LABELS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo']
const WEEKDAY_SHORT_LABELS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom']
const DAY_SECONDS = 86400
const DAYS_IN_WEEK = 7
const SUNDAY_TO_MONDAY_OFFSET = 6

export const tariffUtils = {
  // "17:30:00" -> "17:30"
  formatTime(time: string): string {
    return time?.slice(0, 5) ?? ''
  },

  toDaySeconds(time: string): number {
    const [hours, minutes, seconds] = (time ?? '').split(':').map(Number)
    return (hours || 0) * 3600 + (minutes || 0) * 60 + (seconds || 0)
  },

  nowInDaySeconds(): number {
    const now = new Date()
    return now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds()
  },

  findCurrentSegmentIndex(segments: TariffTimelineSegment[] | undefined, daySeconds: number): number {
    return (
      segments?.findIndex((segment) => {
        const start = this.toDaySeconds(segment.start)
        const end = this.toDaySeconds(segment.end) || DAY_SECONDS

        return daySeconds >= start && daySeconds < end
      }) ?? -1
    )
  },

  formatTimeRange(startTime: string, endTime: string): string {
    if (startTime === endTime) return 'Dia inteiro'

    return `${this.formatTime(startTime)}–${this.formatTime(endTime)}`
  },

  // Nome da faixa na linha do tempo. Trecho sem regra não tem nome de faixa para mostrar:
  // ali vale o preço do próprio conector, e dizer isso é mais honesto do que repetir um
  // rótulo interno.
  formatSegmentLabel(segment: TariffTimelineSegment): string {
    return segment.source === 'fallback' ? 'Tarifa padrão do carregador' : segment.label
  },

  currentWeekday(): number {
    return (new Date().getDay() + SUNDAY_TO_MONDAY_OFFSET) % DAYS_IN_WEEK
  },

  formatWeekday(weekday: number): string {
    return WEEKDAY_LABELS[weekday] ?? ''
  },

  formatDayLabel(day: TariffWeekDay): string {
    return day.is_today ? 'Hoje' : this.formatWeekday(day.weekday)
  },

  formatWeekdayShort(weekday: number): string {
    return WEEKDAY_SHORT_LABELS[weekday] ?? ''
  },

  formatDayShortLabel(day: TariffWeekDay): string {
    return day.is_today ? 'Hoje' : this.formatWeekdayShort(day.weekday)
  },

  formatDayDate(day: TariffWeekDay): string {
    const [, month, dayOfMonth] = day.date?.split('-') ?? []
    return month && dayOfMonth ? `${dayOfMonth}/${month}` : ''
  },

  hasChargeInTimeline(timeline: TariffTimelineSegment[] | null | undefined, fallbackRate: number): boolean {
    if (!timeline?.length) return fallbackRate > 0

    return timeline.some((segment) => Number(segment.rate) > 0)
  },

  hasVaryingRates(timeline: TariffTimelineSegment[] | null | undefined): boolean {
    return new Set(timeline?.map((segment) => Number(segment.rate)) ?? []).size > 1
  },

  hasDynamicTariff(timeline: TariffTimelineSegment[] | null | undefined): boolean {
    return timeline?.some((segment) => segment.source === 'rule') ?? false
  },

  hasDynamicTariffInWeek(week: TariffWeekDay[] | null | undefined): boolean {
    return week?.some((day) => this.hasDynamicTariff(day.segments)) ?? false
  },
}
